import React from "react";
import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import AnimatedPage from "../AnimatedPage";
import styled from "styled-components";
import "animate.css/animate.min.css";
import "../App.css";

function OmPage() {
  const [isMobile, setIsMobile] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [pictures] = useState(["bild1.png", "bild2.png", "bild17.png", "bild18.png"]);
  const [pictures2] = useState(["bild3.png", "bild19.png", "bild5.png"]);
  const [pictures3] = useState(["bild20.png", "bild21.png", "bild22.png", "bild23.png", "bild24.png"]);
  const [pictures4] = useState(["bild11.png", "bild12.png", "bild25.png"]);
  const [currentPicIndex, setCurrentPicIndex] = useState(0);
  const [currentPicIndex2, setCurrentPicIndex2] = useState(0);
  const [currentPicIndex3, setCurrentPicIndex3] = useState(0);
  const [currentPicIndex4, setCurrentPicIndex4] = useState(0);
  const [visibleSections, setVisibleSections] = useState(new Set());

  const heroRef = useRef(null);
  const timelineRef = useRef(null);
  const progressRef = useRef(null);
  const sectionRefs = useRef([]);

  let navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Timeline progress
      if (timelineRef.current && progressRef.current) {
        const rect = timelineRef.current.getBoundingClientRect();
        const timelineTop = rect.top + window.scrollY;
        const timelineHeight = rect.height;
        const scrollPos = window.scrollY + window.innerHeight * 0.75;
        let factor = (scrollPos - timelineTop) / timelineHeight;
        factor = Math.max(0, Math.min(factor, 1));
        progressRef.current.style.height = `${factor * timelineHeight}px`;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleSections((prev) => new Set([...prev, entry.target.dataset.id]));
          }
        });
      },
      { threshold: 0.15 }
    );

    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });

    return () => observer.disconnect();
  }, []);

  function handleResize() {
    setIsMobile(window.innerWidth < 1000);
  }

  const addSectionRef = (el, index) => {
    sectionRefs.current[index] = el;
  };

  let element = document.querySelector(".bosse");

  if (element) {
    element.remove();
  }

  const heroParallax = scrollY * 0.5;
  const heroOpacity = Math.max(0, 1 - scrollY / 600);
  const heroScale = 1 + scrollY * 0.0003;

  return (
    <div style={styles.root}>
      {/* ─── PRELOAD CAROUSEL IMAGES ─── */}
      <div style={{ position: "absolute", width: 0, height: 0, overflow: "hidden", visibility: "hidden" }}>
        {[...pictures, ...pictures2, ...pictures3, ...pictures4].map((src) => (
          <img key={src} src={src} alt="" />
        ))}
      </div>

      {/* ─── HERO ─── */}
      <div style={styles.hero}>
        <div
          style={{
            ...styles.heroBg,
            transform: `scale(${heroScale}) translateY(${heroParallax * 0.4}px)`,
          }}
        >
          <img src="ellinor2.jpg" alt="Ellinor" style={styles.heroImg} />
          <div style={styles.heroOverlay} />
        </div>

        <div style={{ ...styles.heroContent, opacity: heroOpacity }}>
          <div
            style={{
              ...styles.scrollIndicator,
              transform: `translateY(${-heroParallax * 0.1}px)`,
            }}
            onClick={() => window.scrollBy({ top: window.innerHeight, behavior: "smooth" })}
          >
            <div style={styles.scrollLine} />
            <span style={styles.scrollText}>Scrolla</span>
          </div>
        </div>
      </div>

      {/* ─── INTRO SECTION ─── */}
      <section
        ref={(el) => addSectionRef(el, 0)}
        data-id="intro"
        style={{
          ...styles.introSection,
          opacity: visibleSections.has("intro") ? 1 : 0,
          transform: visibleSections.has("intro") ? "translateY(0)" : "translateY(60px)",
        }}
      >
        <div style={styles.introInner}>
          <div style={styles.introText}>
            <div style={styles.sectionTag}>Om mig</div>
            <p style={{ ...styles.introBody, fontSize: isMobile ? 30 : "clamp(18px, 2.2vw, 24px)" }}>
              Jag heter Ellinor, är uppvuxen i Skivarp i södra Skåne och bor i Skurup. Musiken har alltid
              varit en självklar del av mitt liv. Jag erbjuder sång och musik till bröllop, dop,
              begravningar, mingel, fester och andra tillställningar.
            </p>
            <p style={{ ...styles.introBody, fontSize: isMobile ? 25 : "clamp(18px, 2.2vw, 24px)" }}>
              För mig är varje uppdrag unikt, oavsett om det handlar om livets största glädje eller ett
              stillsamt farväl. Det är en ära att få bidra med musik som berör och betyder något på riktigt.
            </p>
          </div>
          <div style={styles.introImageWrap}>
            <img src="IMG_4812.jpeg" alt="Ellinor" style={styles.introImage} />
            <span style={styles.photoCredit}>Photo: Anna Falck</span>
            <div style={styles.introImageDecor} />
          </div>
        </div>
      </section>

      {/* ─── PARALLAX DIVIDER 1 ─── */}
      <div style={styles.parallaxSection}>
        <div
          style={{
            ...styles.parallaxBg,
            backgroundImage: "url('IMG_4821.jpeg')",
            transform: `translateY(${(scrollY - 1200) * 0.3}px)`,
          }}
        />
        <div style={styles.parallaxOverlay}>
          <div style={styles.parallaxQuote}>
            "Musik som berör och betyder något på riktigt"
          </div>
          <div style={styles.parallaxSub}>— Ellinor Nielsen</div>
        </div>
        <span style={styles.parallaxCredit}>Photo: Anna Falck</span>
      </div>

      {/* ─── SAMARBETE SECTION ─── */}
      <section
        ref={(el) => addSectionRef(el, 1)}
        data-id="mans"
        data-theme="dark"
        style={{
          ...styles.darkSection,
          opacity: visibleSections.has("mans") ? 1 : 0,
          transform: visibleSections.has("mans") ? "translateY(0)" : "translateY(60px)",
          transition: "all 1s ease 0.2s",
        }}
      >
        <div style={styles.darkInner}>
          <div style={styles.sectionTag}>Samarbete</div>
          <h2 style={styles.darkTitle}>Ellinor & Måns</h2>
          <p style={styles.darkBody}>
            Idag samarbetar jag med musikern Måns Lindblom. Ett bröllop blev starten på ett fint
            musikaliskt samarbete. Vi erbjuder levande musik med stor bredd och känsla, alltid anpassad
            efter publik och sammanhang – från country och pop till schlager och dansband.
          </p>
          <p style={styles.darkBody}>
            Vår styrka är förmågan att läsa rummet och skapa rätt stämning. Utöver musiken har vi båda
            lång erfarenhet av att möta människor – Måns är psykolog och jag är socionom.
          </p>
          <a
            href="https://ellinor-mans-hub.lovable.app/"
            target="_blank"
            rel="noopener noreferrer"
            style={styles.linkButton}
          >
            Lyssna på oss →
          </a>
        </div>
      </section>

      {/* ─── PARALLAX DIVIDER 2 – elliliten.png ─── */}
      <div style={{ ...styles.parallaxSection, height: isMobile ? "50vh" : "65vh" }}>
        <div
          style={{
            ...styles.parallaxBg,
            backgroundImage: "url('elliliten.png')",
            backgroundPosition: "center 20%",
            transform: `translateY(${(scrollY - 1800) * 0.15}px)`,
          }}
        />
        <div style={styles.parallaxOverlay} />
      </div>

      {/* ─── MIN RESA INTRO ─── */}
      <section
        ref={(el) => addSectionRef(el, 2)}
        data-id="resa"
        style={{
          ...styles.creamSection,
          opacity: visibleSections.has("resa") ? 1 : 0,
          transform: visibleSections.has("resa") ? "translateY(0)" : "translateY(60px)",
          transition: "all 1s ease",
        }}
      >
        <div style={styles.creamInner}>
          <div style={styles.sectionTag}>Min resa</div>
          <h2 style={styles.creamTitle}>Hur allt började</h2>
          <p style={styles.creamBody}>
            Musiken har alltid varit en viktig del av mitt liv. Jag började sjunga i kyrkans barnkör när
            jag var 6 år och minns att jag då inte var särskilt bekväm med att stå framför andra och
            sjunga. Jag hade mest sjungit hemma framför spegeln.
          </p>
          <p style={styles.creamBody}>
            Tack vare stöd från min körledare och min familj vågade jag ta mitt första solo – och jag
            blev mer och mer bekväm med att sjunga för andra.
          </p>
        </div>
      </section>

      {/* ─── TIMELINE ─── */}
      <section style={styles.timelineSection}>
        <div style={styles.timelineContainer} ref={timelineRef}>
          {/* Vertical line */}
          <div style={styles.timelineLine}>
            <div ref={progressRef} style={styles.timelineProgress} />
          </div>

          {/* 2006 */}
          <TimelineItem
            year="2006"
            title="Den första gitarren"
            body="Jag minns hur mina kompisar önskade sig de senaste mobiltelefonerna, men jag ville ha en gitarr. När jag fyllde 10 år fick jag min allra första. Min stora förebild var Rosa i serien Livet enligt Rosa – hon spelade gitarr och skrev egna låtar och jag ville göra precis samma sak."
            image="elliliten.png"
            caption="På min 10-årsdag när jag hittade min allra första gitarr."
            imageLeft={false}
            isMobile={isMobile}
          />

          {/* 2012 */}
          <TimelineItem
            year="2012"
            title="Musikalen"
            body="År 2012 fick jag den stora äran att vara med i musikalen Hjärtan mellan stängsel i Skurups församling, där jag fick en av huvudrollerna. Musikalen handlar om förbjuden kärlek – hur en SS-soldat förälskar sig i en judisk kvinna."
            imageLeft={true}
            isMobile={isMobile}
            gallery={pictures}
            currentIndex={currentPicIndex}
            setIndex={setCurrentPicIndex}
            link="https://www.ystadsallehanda.se/skurup/fangslande-toner-mot-fordomar/"
            linkText="Läs mer om föreställningen"
          />

          {/* 2014 */}
          <TimelineItem
            year="2014"
            title="STOP it with music"
            body="Sista året på gymnasiet valde jag att skriva om hur musik påverkar oss – men jag ville också testa det i verkligheten. Projektet fick namnet STOP it with music – Stoppa cancer med musik som verktyg. Tack vare alla som bokade mig och trodde på projektet samlade vi ihop mer än 30 000 kronor till Ung Cancer."
            imageLeft={false}
            isMobile={isMobile}
            gallery={pictures2}
            currentIndex={currentPicIndex2}
            setIndex={setCurrentPicIndex2}
            caption2={[
              "Jag delade ut broschyrer till företag, kyrkor och organisationer. Alla intäkter gick oavkortat till Ung Cancer.",
              "2015-01-08 – mitt arbete uppmärksammades i Radio Active.",
              "Skurups kyrka, STOP it with music 2014-03-16.",
            ]}
            currentIndex2={currentPicIndex2}
          />
          <TimelineItem
            year="2014"
            title="Efter studenten"
            body="År 2014 tog jag studenten. Jag fortsatte att sjunga och spela i olika sammanhang under sommaren, samtidigt som jag arbetade med annat vid sidan om. Jag ville utveckla min idé STOP it with music och under hösten 2014 gick jag en utbildning för att lära mig mer om hur man startar och driver företag."
            imageLeft={false}
            isMobile={isMobile}
            gallery={pictures3}
            currentIndex={currentPicIndex3}
            setIndex={setCurrentPicIndex3}
            caption2={[
              "Studenten 2014.",
              "Svedala marknad juni 2014-06-28.",
              "Underhållning på sommarcafé i Skurups församlings trädgård 2014-07-22.",
              "",
              "Och i publiken satt ofta min mormor som varit ett stort stöd för mig, som peppat och trott på mig alltid och som varit med mig på mina spelningar så länge hon kunde. Hon sjöng alltid med låtarna jag sjöng."
            ]}
            currentIndex3={currentPicIndex3}
          />

          {/* AFTER STUDENT */}
          <div style={styles.timelineItemWrap}>
            <div style={styles.timelineYearBadge}>
              <span style={styles.timelineYear}>2014+</span>
            </div>
            <div style={{ maxWidth: 700, marginLeft: isMobile ? 40 : 80 }}>
              <h3 style={styles.timelineTitle}>Efter studenten</h3>
              <p style={styles.timelineBody}>
                År 2014 tog jag studenten. Jag fortsatte att sjunga och spela i olika sammanhang under
                sommaren. Under hösten 2014 gick jag en utbildning för att lära mig mer om hur man startar
                och driver företag.
              </p>
              {/* Pictures 4 carousel */}
              <div style={{ position: "relative", marginTop: 32 }}>
                <div style={{
                  width: "90vw",
                  maxWidth: isMobile ? 780 : 480,
                  aspectRatio: "4/3",
                  borderRadius: 8,
                  overflow: "hidden",
                  background: "#1a1410",
                }}>
                  <img
                    key={currentPicIndex4}
                    src={pictures4[currentPicIndex4]}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain",
                      animation: "carouselFade 0.4s ease",
                    }}
                    onClick={() => setCurrentPicIndex4((i) => (i + 1) % pictures4.length)}
                  />
                </div>
                <CarouselButtons
                  onPrev={() => setCurrentPicIndex4((i) => (i - 1 + pictures4.length) % pictures4.length)}
                  onNext={() => setCurrentPicIndex4((i) => (i + 1) % pictures4.length)}
                />
                <p style={{ ...styles.caption, fontSize: isMobile ? 9 : 13 }}>
                  {currentPicIndex4 === 0 && "Från nyöppningen på Jysk i november 2014"}
                  {currentPicIndex4 === 1 && "Från en av mina spelningar för Jysk. Kalmar 2015-10-30"}
                  {currentPicIndex4 === 2 && "2016-02-25. Väla i Helsingborg"}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
        <Social>
                    <div className="content">
                      <Link to="/spelningar" className="link">
                        <div className="card">
                          <div className="icon"></div>
                          <p className="title">Lyssna</p>
                          <p className="text"></p>
                        </div>
                      </Link>
        
                      <Link to="/bokning" className="link">
                        <div className="card">
                          <div className="icon"></div>
                          <p className="title">Bokning</p>
                          <p className="text"></p>
                        </div>
                      </Link>
                    </div>
                  </Social>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=Jost:wght@300;400;500&display=swap');

        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { overflow-x: hidden; }

        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(40px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes scrollPulse {
          0%,100% { transform: scaleY(1); opacity: 0.5; }
          50%      { transform: scaleY(1.5); opacity: 1; }
        }
        @keyframes timelineDot {
          from { transform: scale(0); }
          to   { transform: scale(1); }
        }
        @keyframes carouselFade {
          from { opacity: 0; transform: scale(1.03); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}

/* ── TIMELINE ITEM COMPONENT ── */
function TimelineItem({ year, title, body, image, caption, imageLeft, isMobile, gallery, currentIndex, setIndex, link, linkText, caption2, currentIndex2 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.12 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      style={{
        ...styles.timelineItemWrap,
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(60px)",
        transition: "all 0.9s ease",
      }}
    >
      <div style={styles.timelineYearBadge}>
        <div style={styles.timelineDot} />
        <span style={styles.timelineYear}>{year}</span>
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : imageLeft ? "row-reverse" : "row",
          gap: 40,
          alignItems: "flex-start",
          marginLeft: isMobile ? 40 : 80,
          maxWidth: 900,
        }}
      >
        <div style={{ flex: 1 }}>
          <h3 style={styles.timelineTitle}>{title}</h3>
          <p style={styles.timelineBody}>{body}</p>
          {link && (
            <a href={link} target="_blank" rel="noopener noreferrer" style={styles.timelineLink}>
              {linkText} →
            </a>
          )}
        </div>

        {(image || gallery) && (
          <div style={{ flex: 1, position: "relative" }}>
            {gallery ? (
              <>
                <div style={{
                  width: "90vw",
                  maxWidth: isMobile ? 780 : 480,
                  aspectRatio: "4/3",
                  borderRadius: 8,
                  overflow: "hidden",
                  position: "relative",
                  background: "#1a1410",
                }}>
                  <img
                    key={currentIndex}
                    src={gallery[currentIndex]}
                    alt=""
                    style={{
                      width: "100%",
                      height: "100%",
                      display: "block",
                      objectFit: "contain",
                      animation: "carouselFade 0.4s ease",
                    }}
                    onClick={() => setIndex((i) => (i + 1) % gallery.length)}
                  />
                </div>
                <CarouselButtons
                  onPrev={() => setIndex((i) => (i - 1 + gallery.length) % gallery.length)}
                  onNext={() => setIndex((i) => (i + 1) % gallery.length)}
                />

                {caption2 && <p style={{ ...styles.caption, fontSize: isMobile ? ((caption2[currentIndex] || "").length > 80 ? 16 : 24) : 13 }}>{caption2[currentIndex] || ""}</p>}
              </>
            ) : (
              <>
                <img src={image} alt="" style={{ width: "90vw", maxWidth: isMobile ? 780 : 400, borderRadius: 8, display: "block", objectFit: "cover" }} />
                {caption && <p style={{ ...styles.caption, fontSize: isMobile ? 24 : 13 }}>{caption}</p>}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── CAROUSEL BUTTONS ── */
function CarouselButtons({ onPrev, onNext }) {
  return (
    <div style={styles.carouselBtns}>
      <button onClick={onPrev} style={styles.carouselBtn}>‹</button>
      <button onClick={onNext} style={styles.carouselBtn}>›</button>
    </div>
  );
}

/* ── STYLES ── */
const COLOR = "#815641";
const CREAM = "#f8f3ee";
const DARK = "#120d09";

const styles = {
  root: {
    fontFamily: "'Jost', sans-serif",
    color: DARK,
    background: "#fff",
    overflowX: "hidden",
    marginTop:"-350px",

  },

  // HERO
  hero: {
    position: "relative",
    height: "100vh",
    minHeight: 600,
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  heroBg: {
    position: "absolute",
    inset: "-10%",
    transformOrigin: "center center",
    transition: "transform 0.05s linear",
  },
  heroImg: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    objectPosition: "40% 25%",
    display: "block",
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(18,13,9,0.2) 0%, rgba(18,13,9,0.55) 60%, rgba(18,13,9,0.85) 100%)",
  },
  heroContent: {
    position: "relative",
    zIndex: 2,
    textAlign: "center",
    color: "#fff",
    padding: "0 20px",
    transition: "opacity 0.1s linear",
  },
  heroEyebrow: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: "clamp(11px, 1.5vw, 14px)",
    letterSpacing: "0.35em",
    textTransform: "uppercase",
    opacity: 0.75,
    marginBottom: 20,
    animation: "fadeUp 1.2s ease 0.3s both",
  },
  heroTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 300,
    fontSize: "clamp(72px, 12vw, 160px)",
    lineHeight: 0.9,
    letterSpacing: "-0.02em",
    marginBottom: 24,
    animation: "fadeUp 1.2s ease 0.5s both",
  },
  heroSub: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: "clamp(14px, 2vw, 18px)",
    letterSpacing: "0.1em",
    opacity: 0.8,
    animation: "fadeUp 1.2s ease 0.7s both",
  },
  scrollIndicator: {
    marginTop: 60,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 12,
    cursor: "pointer",
    animation: "fadeUp 1.2s ease 1s both",
  },
  scrollLine: {
    width: 2,
    height: 60,
    background: "rgba(255,255,255,0.5)",
    animation: "scrollPulse 2s ease-in-out infinite",
  },
  scrollText: {
    fontSize: 24,
    letterSpacing: "0.3em",
    textTransform: "uppercase",
    opacity: 0.6,
  },

  // INTRO
  introSection: {
    padding: "120px 8% 100px",
    background: "#fff",
    transition: "all 1s ease",
  },
  introInner: {
    maxWidth: 1200,
    margin: "0 auto",
    display: "flex",
    gap: "8%",
    alignItems: "center",
    flexWrap: "wrap",
  },
  introText: {
    flex: "1 1 380px",
  },
  introBody: {
    fontFamily: "'Cormorant Garamond', serif",
    fontSize: "clamp(18px, 2.2vw, 24px)",
    lineHeight: 1.75,
    color: "#2a1f18",
    marginBottom: 24,
    fontWeight: 400,
  },
  introImageWrap: {
    flex: "1 1 320px",
    position: "relative",
  },
  introImage: {
    width: "100%",
    maxWidth: 460,
    display: "block",
    borderRadius: 4,
    objectFit: "cover",
    // aspectRatio: "3/4",
  },
  introImageDecor: {
    position: "absolute",
    top: -20,
    right: -20,
    width: "70%",
    height: "70%",
    border: `1px solid ${COLOR}`,
    borderRadius: 4,
    zIndex: -1,
    opacity: 0.35,
  },
  photoCredit: {
    display: "block",
    fontStyle: "italic",
    fontSize: 12,
    color: "#888",
    marginTop: 10,
    letterSpacing: "0.05em",
  },

  // SECTION TAG
  sectionTag: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 400,
    fontSize: 24,
    letterSpacing: "0.4em",
    textTransform: "uppercase",
    color: COLOR,
    marginBottom: 20,
  },

  // PARALLAX
  parallaxSection: {
    position: "relative",
    height: "75vh",
    overflow: "hidden",
  },
  parallaxBg: {
    position: "absolute",
    inset: "-20%",
    backgroundSize: "cover",
    backgroundPosition: "center",
    willChange: "transform",
  },
  parallaxOverlay: {
    position: "absolute",
    inset: 0,
    background: "rgba(18,13,9,0.5)",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 20px",
    textAlign: "center",
  },
  parallaxQuote: {
    fontFamily: "'Cormorant Garamond', serif",
    fontStyle: "italic",
    fontWeight: 300,
    fontSize: "clamp(24px, 5vw, 60px)",
    color: "#fff",
    maxWidth: 800,
    lineHeight: 1.3,
    letterSpacing: "0.02em",
  },
  parallaxSub: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: 14,
    letterSpacing: "0.3em",
    color: "rgba(255,255,255,0.65)",
    marginTop: 20,
    textTransform: "uppercase",
  },
  parallaxCredit: {
    position: "absolute",
    bottom: 16,
    right: 20,
    color: "rgba(255,255,255,0.45)",
    fontSize: 11,
    fontStyle: "italic",
    letterSpacing: "0.05em",
  },
  ctaRow: {
    display: "flex",
    gap: 20,
    marginTop: 40,
    flexWrap: "wrap",
    justifyContent: "center",
  },
  ctaButton: {
    display: "inline-block",
    padding: "14px 40px",
    background: COLOR,
    color: "#fff",
    textDecoration: "none",
    fontFamily: "'Jost', sans-serif",
    fontWeight: 400,
    fontSize: 13,
    letterSpacing: "0.25em",
    textTransform: "uppercase",
    borderRadius: 2,
    transition: "background 0.3s ease",
  },
  ctaButtonOutline: {
    background: "transparent",
    border: "1px solid rgba(255,255,255,0.6)",
    color: "#fff",
  },

  // DARK SECTION
  darkSection: {
    background: DARK,
    padding: "120px 8%",
    transition: "all 1s ease 0.2s",
  },
  darkInner: {
    maxWidth: 700,
    margin: "0 auto",
  },
  darkTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 300,
    fontSize: "clamp(40px, 7vw, 80px)",
    color: "#fff",
    lineHeight: 1,
    marginBottom: 32,
    letterSpacing: "-0.02em",
  },
  darkBody: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: "clamp(15px, 1.8vw, 18px)",
    lineHeight: 1.85,
    color: "rgba(255,255,255,0.65)",
    marginBottom: 20,
  },
  linkButton: {
    display: "inline-block",
    marginTop: 20,
    color: COLOR,
    textDecoration: "none",
    fontFamily: "'Jost', sans-serif",
    fontWeight: 400,
    fontSize: 14,
    letterSpacing: "0.15em",
    borderBottom: `1px solid ${COLOR}`,
    paddingBottom: 2,
    transition: "opacity 0.2s",
  },

  // CREAM SECTION
  creamSection: {
    background: CREAM,
    padding: "120px 8%",
    transition: "all 1s ease",
  },
  creamInner: {
    maxWidth: 700,
    margin: "0 auto",
  },
  creamTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 300,
    fontSize: "clamp(36px, 6vw, 72px)",
    color: DARK,
    lineHeight: 1.05,
    marginBottom: 32,
    letterSpacing: "-0.02em",
  },
  creamBody: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: "clamp(15px, 1.8vw, 18px)",
    lineHeight: 1.85,
    color: "#3a2b22",
    marginBottom: 20,
  },

  // TIMELINE
  timelineSection: {
    background: "#fff",
    padding: "80px 0 120px",
  },
  timelineContainer: {
    maxWidth: 1100,
    margin: "0 auto",
    padding: "0 5%",
    position: "relative",
  },
  timelineLine: {
    position: "absolute",
    left: "5%",
    top: 0,
    width: 2,
    height: "100%",
    background: "rgba(129,86,65,0.15)",
  },
  timelineProgress: {
    position: "absolute",
    top: 0,
    left: 0,
    width: 2,
    height: 0,
    background: COLOR,
    transition: "height 0.2s ease-out",
  },
  timelineItemWrap: {
    paddingTop: 80,
    paddingBottom: 80,
    position: "relative",
  },
  timelineYearBadge: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    marginBottom: 28,
  },
  timelineDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    background: "#fff",
    border: `3px solid ${COLOR}`,
    flexShrink: 0,
  },
  timelineYear: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 400,
    fontSize: "clamp(28px, 4vw, 48px)",
    color: COLOR,
    letterSpacing: "0.02em",
  },
  timelineTitle: {
    fontFamily: "'Cormorant Garamond', serif",
    fontWeight: 600,
    fontSize: "clamp(22px, 3vw, 32px)",
    color: DARK,
    marginBottom: 16,
    lineHeight: 1.15,
  },
  timelineBody: {
    fontFamily: "'Jost', sans-serif",
    fontWeight: 300,
    fontSize: "clamp(14px, 1.6vw, 17px)",
    lineHeight: 1.9,
    color: "#4a3529",
    marginBottom: 20,
  },
  timelineLink: {
    color: COLOR,
    textDecoration: "none",
    fontSize: 14,
    letterSpacing: "0.1em",
    borderBottom: `1px solid ${COLOR}`,
    paddingBottom: 1,
  },
  caption: {
    fontStyle: "italic",
    fontSize: 13,
    color: "#888",
    marginTop: 10,
    lineHeight: 1.5,
  },

  // CAROUSEL
  carouselBtns: {
    display: "flex",
    gap: 10,
    marginTop: 14,
  },
  carouselBtn: {
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: COLOR,
    border: "none",
    color: "#fff",
    fontSize: 22,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    lineHeight: 1,
    fontFamily: "'Cormorant Garamond', serif",
    transition: "background 0.2s",
  },

};

const Social = styled.div`
  z-index: 99;
  .link {
    text-decoration: none;
    position: relative;
    max-width: 200px;
    margin-left: 20px;
  }
  text-decoration: none;
  body {
    width: 100vw;
    background-color: #1d1d1d;
    margin: 0;
    font-family: helvetica;
  }

  .about {
    $cubic: cubic-bezier(0.64, 0.01, 0.07, 1.65);
    $transition: 0.6s $cubic;
    $size: 40px;
    position: fixed;
    z-index: 99;
    bottom: 10px;
    right: 10px;
    width: $size;
    height: $size;
    display: flex;
    justify-content: flex-end;
    align-items: flex-end;
    transition: all 0.2s ease;

    .bg_links {
      width: $size;
      height: $size;
      border-radius: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
      background-color: rgba(#fff, 0.2);
      border-radius: 100%;
      backdrop-filter: blur(5px);
      position: absolute;
    }

    .logo {
      width: $size;
      height: $size;
      z-index: 99;
      background-image: url(https://rafaelalucas91.github.io/assets/codepen/logo_white.svg);
      background-size: 50%;
      background-repeat: no-repeat;
      background-position: 10px 7px;
      opacity: 0.9;
      transition: all 1s 0.2s ease;
      bottom: 0;
      right: 0;
    }

    .social {
      opacity: 0;
      right: 0;
      bottom: 0;

      .icon {
        width: 100%;
        height: 100%;
        background-size: 20px;
        background-repeat: no-repeat;
        background-position: center;
        background-color: transparent;
        display: flex;
        transition: all 0.2s ease, background-color 0.4s ease;
        opacity: 0;
        border-radius: 100%;
      }

      &.portfolio {
        transition: all 0.8s ease;

        .icon {
          background-image: url(https://rafaelalucas91.github.io/assets/codepen/link.svg);
        }
      }

      &.dribbble {
        transition: all 0.3s ease;
        .icon {
          background-image: url(https://rafaelalucas91.github.io/assets/codepen/dribbble.svg);
        }
      }

      &.linkedin {
        transition: all 0.8s ease;
        .icon {
          background-image: url(https://rafaelalucas91.github.io/assets/codepen/linkedin.svg);
        }
      }
    }

    &:hover {
      width: 105px;
      height: 105px;
      transition: all $transition;

      .logo {
        opacity: 1;
        transition: all 0.6s ease;
      }

      .social {
        opacity: 1;

        .icon {
          opacity: 0.9;
        }

        &:hover {
          background-size: 28px;
          .icon {
            background-size: 65%;
            opacity: 1;
          }
        }

        &.portfolio {
          right: 0;
          bottom: calc(100% - 40px);
          transition: all 0.3s 0s $cubic;

          .icon {
            &:hover {
              background-color: #698fb7;
            }
          }
        }

        &.dribbble {
          bottom: 45%;
          right: 45%;
          transition: all 0.3s 0.15s $cubic;
          .icon {
            &:hover {
              background-color: #ea4c89;
            }
          }
        }

        &.linkedin {
          bottom: 0;
          right: calc(100% - 40px);
          transition: all 0.3s 0.25s $cubic;
          .icon {
            &:hover {
              background-color: #0077b5;
            }
          }
        }
      }
    }
  }

  .wrapper {
    width: 100vw;
    margin: 0 auto;
    height: 400px;
    background-color: #161616;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
    transition: all 0.3s ease;
  }

  @media screen and (max-width: 767px) {
    .wrapper {
      height: 700px;
    }
  }

  .content {
    width: 100vw;
    height: 30%;
    padding: 0 4%;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-left: auto;
    margin-right: auto;
    bottom: -5%;
    position: fixed;
    @media screen and (max-width: 1000px) {
      left: -26%;
      height: 20%;
    }

    @media screen and (max-width: 1800px) {
      left: -1%;
    }
  }

  .card {
    width: 50%;
    max-width: 300px;
    min-width: 200px;
    height: 150px;
    background-color: #815641;
    margin: 10px;
    border-radius: 10px;
    box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.24);
    border: 2px solid rgba(7, 7, 7, 0.12);
    font-size: 12px;
    transition: all 0.3s ease;
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;

    .mic {
      margin-left: -200px;
      margin-top: -90px;
      height: 80px;
      width: 80px;
      position: relative;
    }

    cursor: pointer;
    transition: all 0.3s ease;
    :hover {
      margin-top: -120px;
    }
  }

  .icon {
  }

  .material-icons.md-18 {
    font-size: 12px;
  }
  .material-icons.md-24 {
    font-size: 12px;
  }
  .material-icons.md-36 {
    font-size: 28px;
  }
  .material-icons.md-48 {
    font-size: 48px;
  }

  .card .title {
    width: 100%;
    margin: 0;
    text-align: center;
    margin-top: -40px;
    color: white;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 4px;
    font-size: 20px;
  }

  .card .text {
    width: 80%;
    margin: 0 auto;
    font-size: 12px;
    text-align: center;
    margin-top: 20px;
    color: white;
    font-weight: 200;
    letter-spacing: 2px;
    opacity: 0;
    max-height: 0;
    font-family: helvetica !important;
  }

  .card:hover {
    height: 270px;
  }

  .card:hover .info {
    height: 90%;
  }
  .card:hover .info {
    height: 90%;
  }

  .card:hover .text {
    opacity: 1;
    max-height: 40px;
    margin-top: 0px;
    font-size: 15px;
  }

  .card:hover .icon {
    background-position: -120px;
    margin-top: -40px;
    height: 150px;
  }

  .card:hover .title {
    margin-top: -200px;
    margin-bottom: 60px;
  }

  .card:hover .icon i {
    background: linear-gradient(90deg, #ff7e7e, #ff4848);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    opacity: 1;
  }
`;

export default OmPage;
